import React, { useEffect } from 'react';
import { Link } from 'gatsby';

import { useIconSelection } from '../context/IconSelectionContext';

const BODY_CLASS = 'hkx-icon-selector-page';
const CHILD_PAGE_PATH = '/ninos/';
const ADULT_PAGE_PATH = '/adultos/';

const isImageValue = (value) =>
  Boolean(
    value && typeof value === 'object' && !Array.isArray(value) && typeof value.url === 'string'
  );

const isTextValue = (value) => typeof value === 'string' && value.trim().length > 0;

const normalizeData = (image, text, subText) => ({
  image: isImageValue(image) ? image : null,
  text: isTextValue(text) ? text.trim() : '',
  subText: isTextValue(subText) ? subText.trim() : '',
});

const STACKED_TITLE = 'Necesito ayuda para aliviar a un:';
const STACKED_TITLE_LINES = ['Necesito ayuda', 'Para aliviar a un:'];

const getTitleData = (title) => {
  const normalizedTitle = isTextValue(title) ? title.trim() : '';

  if (normalizedTitle.toLowerCase() !== STACKED_TITLE.toLowerCase()) {
    return {
      isStacked: false,
      lines: [normalizedTitle],
    };
  }

  return {
    isStacked: true,
    lines: STACKED_TITLE_LINES,
  };
};

const CHILD_IMAGE_ALT = 'Niño animado con playera azul';
const ADULT_IMAGE_ALT = 'Mujer animada con playera roja';

const IconChoice = ({ image, label, subLabel, variant, alt: altText }) => {
  const alt = altText || label || subLabel || image?.filename || 'Icon';

  return (
    <article className={`hkx-age-selector__option hkx-age-selector__option--${variant}`}>
      <div className="hkx-age-selector__image-wrap">
        {image?.url ? (
          <img
            className="hkx-age-selector__image"
            src={image.url}
            alt={alt}
            width="192"
            height="192"
            loading="lazy"
          />
        ) : null}
      </div>
      <h3 className="hkx-age-selector__label">{label}</h3>
      {subLabel ? <p className="hkx-age-selector__sub-label">{subLabel}</p> : null}
    </article>
  );
};

const KeyValueIcon = (props) => {
  const { textInitial, iconChild, textChild, subTextChild, iconAdult, textAdult, subTextAdult } =
    props;
  const { child, adult, initialText, setChild, setAdult, setInitialText } = useIconSelection();
  const hasInitialText = isTextValue(textInitial);
  const hasChildData =
    isImageValue(iconChild) || isTextValue(textChild) || isTextValue(subTextChild);
  const hasAdultData =
    isImageValue(iconAdult) || isTextValue(textAdult) || isTextValue(subTextAdult);

  useEffect(() => {
    if (!hasInitialText) return;
    setInitialText(textInitial.trim());
  }, [hasInitialText, setInitialText, textInitial]);

  useEffect(() => {
    if (!hasChildData) return;
    setChild(normalizeData(iconChild, textChild, subTextChild));
  }, [hasChildData, iconChild, setChild, subTextChild, textChild]);

  useEffect(() => {
    if (!hasAdultData) return;
    setAdult(normalizeData(iconAdult, textAdult, subTextAdult));
  }, [hasAdultData, iconAdult, setAdult, subTextAdult, textAdult]);

  useEffect(() => {
    if (!hasChildData || typeof document === 'undefined') return undefined;
    document.body.classList.add(BODY_CLASS);

    return () => {
      document.body.classList.remove(BODY_CLASS);
      setChild(null);
      setAdult(null);
      setInitialText('');
    };
  }, [hasChildData, setAdult, setChild, setInitialText]);

  if (!hasChildData) return null;

  const childData = child || normalizeData(iconChild, textChild, subTextChild);
  const adultData = adult || null;
  const titleText = initialText || (hasInitialText ? textInitial.trim() : '');
  const { isStacked, lines: titleLines } = getTitleData(titleText);

  return (
    <section className="hkx-age-selector">
      {titleText ? (
        <h2
          className={`hkx-age-selector__title${
            isStacked ? ' hkx-age-selector__title--stacked' : ''
          }`}
        >
          {titleLines.map((line) => (
            <span key={line} className="hkx-age-selector__title-line">
              {line}
            </span>
          ))}
        </h2>
      ) : null}
      <div className="hkx-age-selector__options">
        <Link className="hkx-age-selector__link" to={CHILD_PAGE_PATH}>
          <IconChoice
            variant="child"
            image={childData?.image}
            label={childData?.text || 'NIÑO'}
            subLabel={childData?.subText}
            alt={CHILD_IMAGE_ALT}
          />
        </Link>
        {adultData ? <div className="hkx-age-selector__divider" aria-hidden="true" /> : null}
        {adultData ? (
          <Link className="hkx-age-selector__link" to={ADULT_PAGE_PATH}>
            <IconChoice
              variant="adult"
              image={adultData?.image}
              label={adultData?.text || 'ADULTO'}
              subLabel={adultData?.subText}
              alt={ADULT_IMAGE_ALT}
            />
          </Link>
        ) : null}
      </div>
    </section>
  );
};

export default KeyValueIcon;
